import { useRef } from 'react';
import { formatRuntime } from '../../utils/filmWall.js';
import styles from './FilmCard.module.css';

// ─── Control bar ──────────────────────────────────────────────────────────────
// The pop-up's play / scrub / mute / fullscreen strip. Shared by the mp4 player
// and the YouTube one, so every film in the card has the same controls.
// It only reports what was asked for (a fraction of the way along, a step in
// seconds); each player decides how to carry that out.
// ─────────────────────────────────────────────────────────────────────────────

const Svg = ({ children }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const IconPlay   = () => <Svg><polygon points="7 4 20 12 7 20 7 4" fill="currentColor" /></Svg>;
const IconPause  = () => <Svg><line x1="8" y1="5" x2="8" y2="19" /><line x1="16" y1="5" x2="16" y2="19" /></Svg>;
const IconMuted  = () => <Svg><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></Svg>;
const IconSound  = () => <Svg><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M19 5a10 10 0 0 1 0 14" /></Svg>;
const IconExpand = () => <Svg><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></Svg>;

export default function ControlBar({
  playing, muted, time, length,
  onToggle, onMute, onFullscreen, onSeekFraction, onSeekBy,
}) {
  const barRef = useRef(null);

  const fractionAt = (clientX) => {
    const bar = barRef.current;
    if (!bar || !Number.isFinite(clientX)) return null;
    const r = bar.getBoundingClientRect();
    return r.width ? Math.min(Math.max((clientX - r.left) / r.width, 0), 1) : null;
  };
  const seekAt = (clientX) => {
    const f = fractionAt(clientX);
    if (f != null) onSeekFraction(f);
  };

  const onBarDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    seekAt(e.clientX);
  };
  const onBarMove = (e) => { if (e.buttons & 1) seekAt(e.clientX); };
  const onBarKey = (e) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.stopPropagation();   // keep ← → for scrubbing while the bar has focus
      onSeekBy(e.key === 'ArrowRight' ? 5 : -5);
    }
  };

  const progress = length ? Math.min((time / length) * 100, 100) : 0;

  return (
    <div className={`${styles.controls} ${playing ? '' : styles.controlsPinned}`}>
      <button type="button" className={styles.ctl} onClick={onToggle} aria-label={playing ? 'Pause' : 'Play'}>
        {playing ? <IconPause /> : <IconPlay />}
      </button>
      <span className={styles.time}>{formatRuntime(time)} / {formatRuntime(length)}</span>
      <div
        ref={barRef}
        className={styles.bar}
        role="slider"
        tabIndex={0}
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={Math.round(length)}
        aria-valuenow={Math.round(time)}
        aria-valuetext={`${formatRuntime(time)} of ${formatRuntime(length)}`}
        onPointerDown={onBarDown}
        onPointerMove={onBarMove}
        onKeyDown={onBarKey}
      >
        <span className={styles.barFill} style={{ width: `${progress}%` }} />
      </div>
      <button type="button" className={styles.ctl} onClick={onMute} aria-label={muted ? 'Unmute' : 'Mute'}>
        {muted ? <IconMuted /> : <IconSound />}
      </button>
      <button type="button" className={styles.ctl} onClick={onFullscreen} aria-label="Fullscreen">
        <IconExpand />
      </button>
    </div>
  );
}
